// "use client";

// import { useState } from "react";
// import Image from "next/image";
// import Layout1 from "./HeroVideoLayouts/Layout1";
// import Layout2 from "./HeroVideoLayouts/Layout2";
// import Layout3 from "./HeroVideoLayouts/Layout3";
// import Layout4 from "./HeroVideoLayouts/Layout4";
// import Layout5 from "./HeroVideoLayouts/Layout5";
// import Layout6 from "./HeroVideoLayouts/Layout6";
// import Layout7 from "./HeroVideoLayouts/Layout7";
// import Layout8 from "./HeroVideoLayouts/Layout8";
// import Layout9 from "./HeroVideoLayouts/Layout9";
// import Layout10 from "./HeroVideoLayouts/Layout10";
// import { Inter } from "next/font/google";

// interface HeroVideoProps {
//   data: {
//     title: string;
//     description: string;
//     videoSource: string;
//     btnLabel: string;
//     btnLink: string;
//     isLive: boolean;
//     posterImage?: string; // optional hero image fallback
//   };
// }

// // Preload font with swap to avoid invisible text
// const inter = Inter({
//   subsets: ["latin"],
//   display: "swap",
// });

// // Explicitly type the layouts map
// const layouts: Record<number, React.FC<HeroVideoProps>> = {
//   1: Layout1,
//   2: Layout2,
//   3: Layout3,
//   4: Layout4,
//   5: Layout5,
//   6: Layout6,
//   7: Layout7,
//   8: Layout8,
//   9: Layout9,
//   10: Layout10,
// };

// export default function HeroVideo({ data }: HeroVideoProps) {
//   const [layout, setLayout] = useState<number>(10);
//   const LayoutComponent = layouts[layout] || Layout1;

//   return (
//     <div className="relative">

//       {/* Development layout switcher */}
//       {process.env.NEXT_PUBLIC_MODE === "DEVELOPMENT" && (
//         <div className="absolute top-4 right-4 z-50 bg-white shadow-md border border-gray-200 rounded px-3 py-2 text-sm">
//           <label
//             htmlFor="layoutSelect"
//             className="block mb-1 font-semibold text-gray-700"
//           >
//             Select Layout
//           </label>
//           <select
//             id="layoutSelect"
//             value={layout}
//             onChange={(e) => setLayout(Number(e.target.value))}
//             className="w-full px-2 py-1 border rounded text-sm text-black"
//           >
//             <option value={1}>Layout 1</option>
//             <option value={2}>Layout 2</option>
//             <option value={3}>Layout 3</option>
//             <option value={4}>Layout 4</option>
//             <option value={5}>Layout 5</option>
//             <option value={6}>Layout 6</option>
//             <option value={7}>Layout 7</option>
//             <option value={8}>Layout 8</option>
//             <option value={9}>Layout 9</option>
//             <option value={10}>Layout 10</option>
//           </select>
//         </div>
//       )}

//       {/* Preload background hero image if available */}
//       {data.posterImage && (
//         <Image
//           src={data.posterImage}
//           alt={data.title}
//           priority
//           fetchPriority="high"
//           fill
//           className="object-cover -z-10"
//         />
//       )}

//       {/* Pass down props to selected layout */}
//       <div className={inter.className}>
//         <LayoutComponent data={data} />
//       </div>
//     </div>
//   );
// }
"use client";

import React, { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import { motion } from "framer-motion";
import Image from "next/image";
import "swiper/css";
import "swiper/css/pagination";

interface HeroVideoProps {
  data: {
    title: string;
    description: string;
    videoSource: string; // multi-line image URLs
    btnLabel: string;
    btnLink: string;
    isLive: boolean;
    posterImage?: string;
  };
}

export default function HeroVideo({ data }: HeroVideoProps) {
  const [isDesktop, setIsDesktop] = useState(false);

  // Split newline-separated URLs into array
  const imageUrls =
    data?.videoSource
      ?.split("\n")
      .map((url) => url.trim())
      .filter(Boolean) || [];

  useEffect(() => {
    const checkIsDesktop = () => setIsDesktop(window.innerWidth >= 1024);
    checkIsDesktop();
    window.addEventListener("resize", checkIsDesktop);
    return () => window.removeEventListener("resize", checkIsDesktop);
  }, []);

  // If section is not live or has no images, don’t render
  if (!data?.isLive || imageUrls.length === 0) return null;

  return (
    <div className={`relative ${isDesktop ? "h-screen" : "h-[60vh]"} w-full`}>
      {/* ✅ Desktop: show first image */}
      {isDesktop ? (
        <Image
            src={imageUrls[0]}
            alt={data.title || "Hero section"}
            fill
            priority
            quality={65}
            sizes="(max-width: 1024px) 100vw, 1600px"
            className="object-cover object-center"
          />
      ) : (
        // ✅ Mobile: use Swiper for remaining images
        <Swiper
          modules={[Autoplay, Pagination]}
          spaceBetween={10}
          slidesPerView={1}
          loop
          pagination={{ clickable: true }}
          autoplay={{ delay: 3000, disableOnInteraction: false }}
          className="w-full h-full"
        >
          {imageUrls.slice(1).map((url: string, index: number) => (
            <SwiperSlide key={index}>
              <Image
                  src={url}
                  alt={`${data.title || "Slide"} ${index + 1}`}
                  fill
                  quality={60}
                  sizes="(max-width: 768px) 100vw, 800px"
                  className="object-cover object-center"
                  loading="lazy"
                />
            </SwiperSlide>
          ))}
        </Swiper>
      )}

      {/* ✅ Overlay Gradient + Text + Button (exact same UI) */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-black/40 flex items-center justify-center">
        <div className="container mx-auto px-6 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto"
          >
            {data.title && (
              <h2 className="text-3xl md:text-4xl   italic mb-4">
                {data.title}
              </h2>
            )}
            {data.description && (
              <p className="text-lg mb-8 opacity-90 leading-relaxed">
                {data.description}
              </p>
            )}
            {data.btnLabel && (
              <a
                href={data.btnLink || "#"}
                className="inline-block bg-white text-black px-6 py-3 rounded-full font-medium hover:bg-gray-200 transition"
              >
                {data.btnLabel}
              </a>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
